import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {ErrorText} from './CreateTriggerDrawerLoaded.styles';
import {MetricNameInput} from './MetricNameInput';
import {RunFilterChunk} from './RunFilterShared';
import {
  type AggOpOption,
  aggOpOptions,
  type CmpOpOption,
  cmpOpOptions,
  isValidMetricName,
  isValidThreshold,
  isValidWindowSize,
  RunMetricThresholdActions,
  useRunMetricThresholdStore,
} from './RunMetricThresholdStore';
import {BoldLabel, SeparatedChunk} from './shared';

// Re-export types and functions from store for backwards compatibility
export type {
  AggOpOption,
  CmpOp,
  CmpOpOption,
  MetricFilter,
  MetricFilterAggOp,
  RunMetricThresholdConfig,
} from './RunMetricThresholdStore';
export {
  aggOpOptions,
  cmpOpOptions,
  getAggOpStr,
  getEventConfigForRunMetric,
  getNumberValueForField,
  getRunMetricThresholdConfig,
  isValidMetricName,
  isValidRunMetricThresholdConfig,
  isValidThreshold,
  isValidWindowSize,
  RunMetricThresholdActions,
  useRunMetricThresholdStore,
} from './RunMetricThresholdStore';

export const RunMetricThresholdSection = () => {
  const metricFilter = useRunMetricThresholdStore(
    useShallow(state => state.metricFilter)
  );
  const runFilter = useRunMetricThresholdStore(
    useShallow(state => state.runFilter)
  );

  // the undefined checks here prevent showing the error before user has configured that field
  const showMetricNameError =
    metricFilter.metricName !== undefined &&
    !isValidMetricName(metricFilter.metricName);
  const showThresholdError =
    metricFilter.threshold !== undefined &&
    !isValidThreshold(metricFilter.threshold);
  const showWindowSizeError =
    metricFilter.windowSize !== undefined &&
    !isValidWindowSize(metricFilter.windowSize);

  const currentAggOpOption =
    aggOpOptions.find(option => option.value === metricFilter.aggOp) ?? null; // note that null tells react-select to show the placeholder, undefined will not

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-wb-border p-16">
        <BoldLabel>Metric</BoldLabel>
        <MetricNameInput
          value={metricFilter.metricName}
          onChange={value => RunMetricThresholdActions.setMetricName(value)}
        />
        {showMetricNameError && (
          <ErrorText>Metric name cannot be empty</ErrorText>
        )}
        <SeparatedChunk>
          <div className="flex items-center">
            <Select<AggOpOption>
              options={aggOpOptions}
              placeholder="Select an aggregation operation..."
              value={currentAggOpOption}
              onChange={(aggOp: AggOpOption | null) => {
                if (aggOp) {
                  RunMetricThresholdActions.setAggOp(aggOp.value);
                }
              }}
              autoFocus={true}
              className="mr-8"
            />{' '}
            of{' '}
            <span className="mx-8 inline-block w-64">
              <TextField
                type="number"
                value={metricFilter.windowSize.toString()}
                onChange={value => {
                  RunMetricThresholdActions.setWindowSize(value);
                }}
                placeholder="Ex. 5"
                errorState={showWindowSizeError}
              />
            </span>
            logged values{' '}
            <Tooltip
              content="Integer 1 - 100 required. Average the metric over a set number of logged values before assessing if a certain threshold has been met."
              trigger={
                <Icon
                  className="inline-block text-moon-400 dark:text-wb-text-tertiary"
                  name="info"
                />
              }
            />
          </div>
          {showWindowSizeError && (
            <ErrorText>Integer 1 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>Metric threshold</BoldLabel>
          <div className="mt-8 flex flex-row gap-16">
            <div className="flex-1">
              <Select<CmpOpOption>
                value={metricFilter.cmpOp}
                onChange={e => RunMetricThresholdActions.setCmpOp(e!)}
                options={cmpOpOptions}
              />
            </div>

            <div className="flex-1">
              <TextField
                value={metricFilter.threshold || ''}
                onChange={value =>
                  RunMetricThresholdActions.setThreshold(value)
                }
                placeholder="Ex. 0.9"
                errorState={showThresholdError}
              />
            </div>
          </div>
          {showThresholdError && (
            <ErrorText>Threshold must be a valid number</ErrorText>
          )}
        </SeparatedChunk>
      </div>
      <SeparatedChunk>
        <RunFilterChunk
          runFilter={runFilter}
          actions={RunMetricThresholdActions}
        />
      </SeparatedChunk>
    </Tailwind>
  );
};
